/*
 * Copyright (c) 2025 Bytedance, Inc. and its affiliates.
 * SPDX-License-Identifier: Apache-2.0
 */
import {
  Coordinates,
  ImageDetailCalculator,
  NormalizeCoordinates,
} from '@ui-tars-test/shared/types';

/**
 * Default coordinate normalization function
 * Normalizes raw coordinates by dividing by 1000 (simple scaling)
 * @param rawCoords - The raw coordinates to normalize
 * @returns Object containing normalized coordinates
 */
export const defaultNormalizeCoords: NormalizeCoordinates = (rawCoords: Coordinates) => {
  if (!rawCoords.raw) {
    return { normalized: rawCoords };
  }
  const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
  const normalizedCoords = {
    ...rawCoords,
    normalized: {
      x: clamp01(rawCoords.raw.x / 1000),
      y: clamp01(rawCoords.raw.y / 1000),
    },
  };
  return { normalized: normalizedCoords };
};

/**
 * Default implementation for detail calculation based on pixel count
 * detail:low mode: 1,048,576 px (1024×1024)
 * detail:high mode: 4,014,080 px (2048×1960)
 */
export const defaultDetailCalculator: ImageDetailCalculator = (
  width: number,
  height: number,
): 'low' | 'high' | 'auto' => {
  // Always use 'high' detail for GUI tasks to ensure text and small elements are visible
  // 'low' detail (512x512) is too small for screen reading
  // 'auto' might choose 'low' for smaller screens incorrectly
  return 'high';
};
